import pick from "lodash/pick";
import APIClient from "../api-client";
import { db } from "../dexie-db";
import { PULL_BATCH_SIZE, PUSH_BATCH_SIZE } from "./constants";
import ServerDownError from "./errors/ServerDown";
import SyncBoxConfig from "./SyncBoxConfig";
import SyncBoxState from "./SyncBoxState";
import { SyncStatus } from "./types";
import { generateSyncBoxId } from "./utils";
export default class SyncBox {
    table;
    config;
    state;
    constructor(config) {
        this.table = db.table(config.collectionName);
        this.config = {
            ...config,
            syncBoxId: generateSyncBoxId(config),
            syncInterval: config.syncInterval ?? 60_000,
            staleThresholdDays: config.staleThresholdDays ?? 30,
            pushConfig: {
                enabled: config.pushConfig?.enabled ?? true,
                fields: config.pushConfig?.fields ?? [],
                batchSize: config.pushConfig?.batchSize ?? PUSH_BATCH_SIZE,
            },
            pullConfig: {
                batchSize: config.pullConfig?.batchSize ?? PULL_BATCH_SIZE,
                filters: config.pullConfig?.filters ?? {},
            }
        };
    }
    initPromise = null;
    async init() {
        if (this.initPromise) {
            return this.initPromise;
        }
        this.initPromise = (async () => {
            const config = new SyncBoxConfig();
            await config.saveConfig(this.config);
            this.state = new SyncBoxState(generateSyncBoxId(this.config));
        })();
        return this.initPromise;
    }
    async getConfig() {
        await this.init();
        return this.config;
    }
    async getState() {
        await this.init();
        return this.state;
    }
    pushChangesPromise = null;
    async pushChanges() {
        if (!this.config.pushConfig.enabled) {
            return;
        }
        // Return existing promise if push is already in progress
        if (this.pushChangesPromise) {
            return this.pushChangesPromise;
        }
        try {
            this.pushChangesPromise = (async () => {
                // Mark all changes that are currently being pushed as pending push
                // This will ensure that we reset the status of changes that might
                // have been picked for pushing but failed to push 
                // (e.g. due to network error, application close, crash, etc.)
                await this.markPushingChangesAsPendingPush();
                let hasMore = true;
                do {
                    const changes = await this.getChangesPendingPush();
                    const batch = changes.slice(0, this.config.pushConfig.batchSize);
                    await this.markChangesAsPushing(batch);
                    if (batch.length < this.config.pushConfig.batchSize) {
                        hasMore = false;
                    }
                    if (batch.length === 0) {
                        continue;
                    }
                    const response = await this.pushChangesToServer(batch);
                    await this.handlePushResponse(batch, response);
                } while (hasMore);
            })();
            await this.pushChangesPromise;
        }
        finally {
            this.pushChangesPromise = null;
        }
    }
    pullChangesPromise = null;
    async pullChanges() {
        // Return existing promise if pull is already in progress
        if (this.pullChangesPromise) {
            return this.pullChangesPromise;
        }
        try {
            this.pullChangesPromise = (async () => {
                let hasMore = true;
                do {
                    const { lastPulledAt, lastPulledId } = await this.state.getLastPulledInfo();
                    const response = await this.pullChangesFromServer(lastPulledAt, lastPulledId);
                    ({ hasMore } = await this.handlePullResponse(response));
                } while (hasMore);
            })();
            return this.pullChangesPromise;
        }
        finally {
            this.pullChangesPromise = null;
        }
    }
    async markPushingChangesAsPendingPush() {
        await this.table.where('_syncBoxId').equals(this.config.syncBoxId)
            .and(item => item._status === SyncStatus.PUSHING)
            .modify(item => {
            item._status = SyncStatus.PENDING_PUSH;
        });
    }
    async getChangesPendingPush() {
        return this.table.where('_syncBoxId').equals(this.config.syncBoxId)
            .and(item => item._status === SyncStatus.PENDING_PUSH)
            .sortBy('utime');
    }
    async markChangesAsPushing(changes) {
        if (changes.length === 0) {
            return;
        }
        await this.table.bulkPut(changes.map(change => ({
            ...change,
            _status: SyncStatus.PUSHING,
        })));
    }
    async pushChangesToServer(changes) {
        try {
            return await APIClient({
                method: 'POST',
                url: `/sync-box/${this.config.remoteCollectionName}/push`,
                data: {
                    documents: Array.isArray(this.config.pushConfig.fields) && this.config.pushConfig.fields.length > 0
                        ? changes.map(change => pick(change, ['_id', 'utime', ...this.config.pushConfig.fields]))
                        : changes
                },
            });
        }
        catch (error) {
            // Convert network/API errors to ServerDownError
            if (error?.response?.status >= 500 || error?.code === 'ECONNREFUSED' || error?.code === 'ENOTFOUND' || error?.code === 'ETIMEDOUT') {
                throw ServerDownError.fromNetworkError(error);
            }
            throw error;
        }
    }
    async handlePushResponse(changes, response) {
        if (response.status !== 200) {
            throw ServerDownError.fromResponse(response.status, response.statusText);
        }
        const { result } = response.data;
        await db.transaction('rw', this.table, async () => {
            await Promise.all(result.map(async (change, index) => {
                const originalChange = changes[index];
                // Find the document that was being pushed using the original _id
                await this.table.where('_id').equals(originalChange._id)
                    // Only modify if the change is still being pushed
                    // This will ensure that we don't suppress local changes to the doc 
                    // that may occur while pushing the changes to the server.
                    .and(item => item._status === SyncStatus.PUSHING)
                    .modify(item => {
                    item._status = SyncStatus.SYNCED;
                    // Update the _id if the server returned a new one (for newly created documents)
                    if (change._id !== originalChange._id) {
                        item._id = change._id;
                    }
                });
            }));
        });
    }
    async pullChangesFromServer(lastPulledAt, lastPulledId) {
        try {
            return await APIClient({
                method: 'GET',
                url: `/sync-box/${this.config.remoteCollectionName}/pull`,
                params: {
                    lastPulledAt,
                    lastPulledId,
                    limit: this.config.pullConfig.batchSize,
                    filters: JSON.stringify(this.config.pullConfig.filters),
                },
            });
        }
        catch (error) {
            // Convert network/API errors to ServerDownError
            if (error?.response?.status >= 500 || error?.code === 'ECONNREFUSED' || error?.code === 'ENOTFOUND' || error?.code === 'ETIMEDOUT') {
                throw ServerDownError.fromNetworkError(error);
            }
            throw error;
        }
    }
    async handlePullResponse(response) {
        if (!response) {
            throw new ServerDownError('No response received from server');
        }
        if (response.status !== 200) {
            throw ServerDownError.fromResponse(response.status, response.statusText);
        }
        const { documents, hasMore, nextLastPulledAt, nextLastPulledId } = response.data;
        await Promise.all(documents.map(async (doc) => {
            await db.transaction('rw', this.table, async () => {
                const existing = await this.table.get({
                    _id: doc._id,
                });
                // Skip if local version is newer
                if (existing && existing.utime > doc.utime) {
                    return;
                }
                await this.table.put({
                    ...doc,
                    _status: SyncStatus.SYNCED,
                    _syncBoxId: this.config.syncBoxId,
                });
            });
        }));
        await this.state.updateLastPulledInfo({
            lastPulledAt: nextLastPulledAt,
            lastPulledId: nextLastPulledId,
        });
        return { hasMore };
    }
}
